import { useEffect, useCallback, useRef, useState } from "react";

interface PerformanceMetrics {
  memoryUsage: number;
  cacheHitRate: number;
  componentsRendered: number;
  imagesLoaded: number;
}

interface PerformanceConfig {
  enableVirtualScrolling?: boolean;
  enableImageOptimization?: boolean;
  enableMemoryManagement?: boolean;
  enableBatteryOptimization?: boolean;
  enableNetworkAwareness?: boolean;
  maxCacheSize?: number;
  cleanupInterval?: number;
}

interface PerformanceState {
  connectionType: string;
  isLowBattery: boolean;
  isSlowNetwork: boolean;
  memoryPressure: "normal" | "high" | "critical";
}

const usePerformanceOptimization = (config: PerformanceConfig = {}) => {
  const metricsRef = useRef<PerformanceMetrics>({
    memoryUsage: 0,
    cacheHitRate: 0,
    componentsRendered: 0,
    imagesLoaded: 0,
  });

  const [performanceState, setPerformanceState] = useState<PerformanceState>({
    connectionType: "unknown",
    isLowBattery: false,
    isSlowNetwork: false,
    memoryPressure: "normal",
  });

  const renderCountRef = useRef(0);
  const imageCountRef = useRef(0);


  const trackPerformanceMetrics = useCallback(() => {
    try {
      if ("memory" in performance) {
        const memory = (performance as any).memory;
        if (memory) {
          const usage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
          metricsRef.current.memoryUsage = usage;

          let pressure: "normal" | "high" | "critical" = "normal";
          if (usage > 80) pressure = "critical";
          else if (usage > 60) pressure = "high";

          setPerformanceState((prev) => ({
            ...prev,
            memoryPressure: pressure,
          }));
        }
      }
    } catch (error) {
      console.warn("Performance monitoring not available");
    }

    metricsRef.current.componentsRendered = renderCountRef.current;
    metricsRef.current.imagesLoaded = imageCountRef.current;
  }, []);


  const trackComponentRender = useCallback((componentName: string) => {
    renderCountRef.current++;

    if (process.env.NODE_ENV === "development") {
      const start = performance.now();
      return () => {
        const end = performance.now();
        console.log(
          `${componentName} rendered in ${(end - start).toFixed(2)}ms`
        );
      };
    }
    return () => {};
  }, []);


  const optimizeImages = useCallback(() => {
    if (!config.enableImageOptimization) return;

    try {
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute("data-src");
                imageObserver.unobserve(img);
                imageCountRef.current++;
              }
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "50px",
        }
      );

      document.querySelectorAll("img[data-src]").forEach((img) => {
        imageObserver.observe(img);
      });

      return () => imageObserver.disconnect();
    } catch (error) {
      console.warn("Image optimization not available");
    }
  }, [config.enableImageOptimization]);


  const createVirtualScrollingHelper = useCallback(
    (containerHeight: number, itemHeight: number, totalItems: number) => {
      if (!config.enableVirtualScrolling) {
        return {
          startIndex: 0,
          endIndex: totalItems,
          totalHeight: totalItems * itemHeight,
          offsetY: 0,
        };
      }

      return {
        getVisibleRange: (scrollTop: number) => {
          const startIndex = Math.floor(scrollTop / itemHeight);
          const visibleCount = Math.ceil(containerHeight / itemHeight) + 2;
          const endIndex = Math.min(startIndex + visibleCount, totalItems);

          return {
            startIndex,
            endIndex,
            totalHeight: totalItems * itemHeight,
            offsetY: startIndex * itemHeight,
          };
        },
      };
    },
    [config.enableVirtualScrolling]
  );


  const getPerformanceReport = useCallback(() => {
    trackPerformanceMetrics();

    return {
      metrics: { ...metricsRef.current },
      state: { ...performanceState },
      recommendations: {
        memoryCleanup: performanceState.memoryPressure !== "normal",
        reducedUpdates:
          performanceState.isLowBattery || performanceState.isSlowNetwork,
        imageOptimization: imageCountRef.current > 20,
      },
    };
  }, [performanceState, trackPerformanceMetrics]);


  useEffect(() => {
    if (config.enableImageOptimization) {
      optimizeImages();
    }


    const metricsInterval = setInterval(trackPerformanceMetrics, 60 * 1000);

    return () => {
      clearInterval(metricsInterval);
    };
  }, [config.enableImageOptimization, optimizeImages, trackPerformanceMetrics]);

  return {
    performanceState,
    metrics: metricsRef.current,
    trackComponentRender,
    createVirtualScrollingHelper,
    getPerformanceReport,
    optimizeImages,
  };
};

export default usePerformanceOptimization;
